import { Plus, Check, Trash2 } from "lucide-react";
import { TYPES, ALL_DAYS } from "../../lib/timetable";

export function ScheduleEditor({ draft, setDraft, onSave }) {
  const setSlotDraft = (i, patch) => setDraft({ ...draft, slots: draft.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const setOvSlot = (d, i, patch) => {
    const ov = { ...(draft.day_overrides || {}) };
    ov[d] = { slots: ov[d].slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) };
    setDraft({ ...draft, day_overrides: ov });
  };
  const setOvSlots = (d, newSlots) => {
    setDraft({ ...draft, day_overrides: { ...(draft.day_overrides || {}), [d]: { slots: newSlots } } });
  };

  return (
    <div className="nb-card p-6 mb-6" data-testid="parent-schedule-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-bold text-lg">Day layout</h2>
        <div className="flex gap-2">
          <button data-testid="parent-add-slot-btn" onClick={() => setDraft({ ...draft, slots: [...draft.slots, { label: "New slot", type: "period", start: "15:00", end: "16:00" }] })}
            className="nb-btn bg-[#FDE68A] px-3 py-1.5 text-sm"><Plus className="w-4 h-4" /> Slot</button>
          <button onClick={onSave} data-testid="parent-save-schedule-btn" className="nb-btn bg-[#A7E4D3] px-4 py-1.5 text-sm"><Check className="w-4 h-4" /> Save layout</button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {ALL_DAYS.map((d) => {
          const active = draft.days.includes(d);
          return (
            <button key={d} type="button" data-testid={`parent-day-toggle-${d.toLowerCase()}`}
              onClick={() => setDraft({ ...draft, days: active ? draft.days.filter((x) => x !== d) : ALL_DAYS.filter((x) => draft.days.includes(x) || x === d) })}
              className={`px-3 py-1.5 rounded-xl border-2 border-zinc-900 font-bold text-xs transition-colors duration-150 ${active ? "bg-[#F9A8D4]" : "bg-white text-zinc-400"}`}>
              {d.slice(0, 3)}
            </button>
          );
        })}
      </div>
      <div className="mt-4 space-y-2">
        {draft.slots.map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-zinc-900 p-2 bg-white" data-testid={`parent-slot-row-${i}`}>
            <input className="nb-input flex-1 min-w-[110px] h-9" value={s.label} data-testid={`parent-slot-label-${i}`} onChange={(e) => setSlotDraft(i, { label: e.target.value })} />
            <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden">
              {TYPES.map((t) => (
                <button key={t.value} type="button" data-testid={`parent-slot-type-${i}-${t.value}`} onClick={() => setSlotDraft(i, { type: t.value })}
                  className={`px-2.5 py-1.5 text-xs font-bold transition-colors duration-150 ${s.type === t.value ? "" : "bg-white text-zinc-400"}`}
                  style={s.type === t.value ? { backgroundColor: t.bg } : {}}>{t.label}</button>
              ))}
            </div>
            <input type="time" className="nb-input w-[105px] h-9" value={s.start} data-testid={`parent-slot-start-${i}`} onChange={(e) => setSlotDraft(i, { start: e.target.value })} />
            <input type="time" className="nb-input w-[105px] h-9" value={s.end} data-testid={`parent-slot-end-${i}`} onChange={(e) => setSlotDraft(i, { end: e.target.value })} />
            <button type="button" data-testid={`parent-slot-delete-${i}`} onClick={() => setDraft({ ...draft, slots: draft.slots.filter((_, j) => j !== i) })}
              className="p-2 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150"><Trash2 className="w-4 h-4 text-red-700" /></button>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t-2 border-zinc-100">
        <span className="nb-label">Custom day layouts (e.g. early-finish Fridays)</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {draft.days.map((d) => (
            <button key={d} type="button" data-testid={`parent-override-day-${d.toLowerCase()}`}
              onClick={() => {
                if ((draft.day_overrides || {})[d]) return;
                setDraft({ ...draft, day_overrides: { ...(draft.day_overrides || {}), [d]: { slots: JSON.parse(JSON.stringify(draft.slots)) } } });
              }}
              className={`px-3 py-1.5 rounded-xl border-2 border-zinc-900 text-xs font-bold transition-colors duration-150 ${(draft.day_overrides || {})[d] ? "bg-[#D8B4FE]" : "bg-white text-zinc-500"}`}>
              {d}{(draft.day_overrides || {})[d] ? " ✎" : ""}
            </button>
          ))}
        </div>
        {Object.entries(draft.day_overrides || {}).map(([d, ov]) => (
          <div key={d} className="mt-3 rounded-xl border-2 border-zinc-900 p-3 bg-white" data-testid={`parent-override-${d.toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-sm">{d} layout</span>
              <div className="flex gap-2">
                <button type="button" data-testid={`parent-override-add-${d.toLowerCase()}`}
                  onClick={() => setOvSlots(d, [...ov.slots, { label: "Club", type: "period", start: "12:30", end: "13:30" }])}
                  className="nb-btn bg-white px-2.5 py-1 text-xs"><Plus className="w-3.5 h-3.5" /></button>
                <button type="button" data-testid={`parent-override-remove-${d.toLowerCase()}`}
                  onClick={() => { const next = { ...(draft.day_overrides || {}) }; delete next[d]; setDraft({ ...draft, day_overrides: next }); }}
                  className="p-1.5 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200"><Trash2 className="w-3.5 h-3.5 text-red-700" /></button>
              </div>
            </div>
            <div className="mt-2 space-y-2">
              {ov.slots.map((s, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`parent-override-slot-${d.toLowerCase()}-${i}`}>
                  <input className="nb-input flex-1 min-w-[100px] h-8 text-xs" value={s.label} onChange={(e) => setOvSlot(d, i, { label: e.target.value })} />
                  <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden">
                    {TYPES.map((t) => (
                      <button key={t.value} type="button" onClick={() => setOvSlot(d, i, { type: t.value })}
                        className={`px-2 py-1 text-[11px] font-bold transition-colors duration-150 ${s.type === t.value ? "" : "bg-white text-zinc-400"}`}
                        style={s.type === t.value ? { backgroundColor: t.bg } : {}}>{t.label}</button>
                    ))}
                  </div>
                  <input type="time" className="nb-input w-[95px] h-8 text-xs" value={s.start} onChange={(e) => setOvSlot(d, i, { start: e.target.value })} />
                  <input type="time" className="nb-input w-[95px] h-8 text-xs" value={s.end} onChange={(e) => setOvSlot(d, i, { end: e.target.value })} />
                  <button type="button" onClick={() => setOvSlots(d, ov.slots.filter((_, j) => j !== i))}
                    className="p-1.5 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200"><Trash2 className="w-3.5 h-3.5 text-red-700" /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
